import { Component, DestroyRef, ElementRef, inject, input, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatListItem, MatNavList } from '@angular/material/list';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogContent } from '@angular/material/dialog';
import { DialogComponent } from '../dialog/dialog.component';
import { DialogChangesService } from '../../../services/dialog/dialog-changes.service';
import { first } from 'rxjs';
import { BaseSettingsDialogTestIds } from './base-settings-dialog.test-ids';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { HeaderActionsService } from '../../../services/dialog/header-actions-service';

@Component({
  selector: 'app-base-dialog',
  imports: [
    RouterLink,
    RouterOutlet,
    RouterLinkActive,
    MatIcon,
    MatNavList,
    MatListItem,
    MatDialogContent,
    AsyncPipe,
    NgTemplateOutlet
  ],
  templateUrl: './base-settings-dialog.component.html',
  styleUrls: ['./base-settings-dialog.component.scss']
})
/**
 * Custom Component für das Erstellen von Cards.
 * Begründung: Dies ist lediglich eine CustomComponent.
 * Es wird keine AFO dadurch erfüllt.
 */
export class BaseSettingsDialogComponent implements OnInit, OnDestroy {
  readonly dialogContainerRef = viewChild.required<ElementRef>('dialogContainer');
  dialogTitle = 'Default Title';
  tabs: { label: string; path: string }[] = [];
  backButtonText = 'Zurück';
  showChevron = true;
  roomId = input.required<string>();
  readonly headerActionsService = inject(HeaderActionsService);
  protected readonly testIds = BaseSettingsDialogTestIds;
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogChangesService = inject(DialogChangesService);

  ngOnInit(): void {
    this.dialogChangesService.setChanges(false);
    const routeData = this.route.snapshot.data;
    this.tabs = routeData['tabs'] ?? [];
    this.dialogTitle = routeData['dialogTitle'] ?? 'Default Title';
    this.backButtonText = routeData['backButtonText'] ?? 'Zurück';
    this.showChevron = routeData['showChevron'] ?? true;
    this.route.firstChild?.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(segments => {
      const activeTab = this.tabs.find(tab => tab.path === segments[0]?.path);
      if (activeTab) {
        this.dialogTitle = activeTab.label;
      }
    });

    // Der Dialog wird in den Fokus gesetzt, um eine Tastatur-Bedienung zu ermöglichen.
    const container = this.dialogContainerRef().nativeElement;
    const firstFocusable = container.querySelector('button') as HTMLElement;
    firstFocusable?.focus();
  }

  ngOnDestroy(): void {
    this.dialogChangesService.setChanges(false);
  }

  updateTitle(newTitle: string): void {
    this.dialogTitle = newTitle;
    this.dialogChangesService.setChanges(false);
  }

  closeDialog(): void {
    this.dialogChangesService.hasChanges$.pipe(takeUntilDestroyed(this.destroyRef), first()).subscribe(hasChanges => {
      if (!hasChanges) {
        this.router.navigate(['../'], { relativeTo: this.route });
        return;
      }
      const dialogRef = this.dialog.open(DialogComponent, {
        data: {
          title: 'Bestätigung',
          message: 'Dialog wird geschlossen, Ihre Änderungen werden nicht gespeichert',
          confirmText: 'OK',
          cancelText: 'Abbrechen',
          cancelDataTestId: 'button_cancel_closeDialog',
          confirmDataTestId: 'button_confirm_closeDialog',
          dialogContentTestId: 'dialog_closeDialog_content',
          dialogActionTestId: 'dialog_closeDialog_actions'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.router.navigate(['../'], { relativeTo: this.route });
        }
      });
    });
  }
}
